var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LoginSource } from "healthex-common/heap.dtos";
import { computed, observable } from "mobx";
import { Disposer } from "../Infra/Disposer";
import { getObservableT } from "../Infra/i18next";
import { ClearAuthState } from "../Infra/Persistence";
import { ClearInitialScreen } from "./ClearAuthManager";
import { NamedRoutes } from "./NamedRoutes";
import { determineOrgId, PatientConsentViewModel } from "./PatientConsentViewModel";
import { OTPClearLoginState, VerifyOTPState } from "./VerifyOTPManager";
const t = getObservableT("patients");
export class PatientLoginVerifyPhoneViewModel extends PatientConsentViewModel {
    constructor(container) {
        super(container);
        this.disposer = new Disposer();
        this.hasClearError = false;
        this.verifyOTPManager = container.verifyOTPManager;
        this.clearAuthManager = container.clearAuthManager;
        this.loginUIManager = container.loginUIManager;
        this.persistence = container.persistence;
        this.socialLoginAuthManager = container.socialLoginAuthManager;
        this.accountSetupCompletionManager = container.accountSetupCompletionManager;
        this.bannerUIManager = container.bannerUIManager;
        this.routingUIManager = container.routingUIManager;
    }
    run() {
        this.verifyOTPManager.resetVerificationFlow();
    }
    stop() {
        this.disposer.dispose();
    }
    get isSandboxLogin() {
        return this.persistence.isSandboxLogin();
    }
    get formModel() {
        return this.verifyOTPManager.formModel;
    }
    get maskedPhoneNumber() {
        return this.verifyOTPManager.getMaskedPhoneNumber();
    }
    get verifyOTPState() {
        return this.verifyOTPManager.verifyOTPState;
    }
    get sendOTPState() {
        return this.verifyOTPManager.sendOTPState;
    }
    get resendCooldownSecondsLeft() {
        return this.verifyOTPManager.resendCooldownSecondsLeft;
    }
    get isLoggingIn() {
        return this.clearAuthManager.state === ClearAuthState.IN_PROGRESS;
    }
    get isClearLoginSuccess() {
        return this.clearAuthManager.state === ClearAuthState.SUCCESS;
    }
    get clearLoginState() {
        var _a;
        if (this.hasClearError)
            return OTPClearLoginState.CLEAR_ERROR;
        const isIAL2Expired = (_a = this.patientConsentUIManager.getIAL2Info()) === null || _a === void 0 ? void 0 : _a.ial2Expired;
        if (isIAL2Expired === true)
            return OTPClearLoginState.CLEAR_EXPIRED;
        if (isIAL2Expired === null)
            return OTPClearLoginState.LOADING;
        return OTPClearLoginState.DEFAULT;
    }
    get isClearLoggingIn() {
        return this.clearAuthManager.state === ClearAuthState.IN_PROGRESS;
    }
    get initialScreen() {
        switch (this.verifyOTPManager.verifyOTPState) {
            case VerifyOTPState.LOCKOUT:
                return ClearInitialScreen.OTP_LOCKOUT;
            default:
                return ClearInitialScreen.OTP_VERIFY_PASSCODE;
        }
    }
    async sendVerificationCode() {
        await this.verifyOTPManager.sendVerificationCode();
    }
    async submitVerificationCode() {
        const verified = await this.verifyOTPManager.submitVerificationCode();
        if (!verified) {
            return;
        }
        await this.completeVerification();
    }
    resetVerifyingStateAfterFailedCompletion() {
        if (this.verifyOTPManager.verifyOTPState === VerifyOTPState.VERIFYING) {
            this.verifyOTPManager.verifyOTPState = VerifyOTPState.INITIAL;
        }
    }
    async completeVerification() {
        const { input } = this.accountSetupCompletionManager;
        const result = await this.socialLoginAuthManager.completeVerificationFlow({
            firstName: input.firstName,
            lastName: input.lastName,
            dateOfBirth: input.birthday,
            phone: input.mobilePhone,
        });
        if (result.ok) {
            this.accountSetupCompletionManager.trackMobileVerified(result.isFirstTimeLogin, result.gfidProvider);
            this.verifyOTPManager.pendingVerificationKey = null;
            this.verifyOTPManager.pendingVerificationPhone = null;
            this.loginUIManager.completeSetupFlowAndEndLogin();
            this.socialLoginAuthManager.resetAuth();
            return;
        }
        this.resetVerifyingStateAfterFailedCompletion();
        if (result.code === "no_pending_data") {
            this.bannerUIManager.displayBanner({
                type: "ERROR",
                text: t("Something went wrong completing your login. Please try again."),
                durationInMS: 6000,
            });
            this.socialLoginAuthManager.resetAuth();
            this.loginUIManager.pendingSetupCompletion = false;
            this.loginUIManager.pendingVerifyPhone = false;
        }
        else {
            this.loginUIManager.pendingSetupCompletion = false;
            this.loginUIManager.pendingVerifyPhone = false;
            this.routingUIManager.navigateTo(NamedRoutes.LOGIN_AS_PATIENT);
        }
    }
    async tryADifferentPhoneNumber() {
        this.loginUIManager.pendingSetupCompletion = true;
        this.loginUIManager.pendingVerifyPhone = false;
    }
    async verifyWithClear(isSandbox = false) {
        this.persistence.setSandboxLogin(isSandbox);
        await this.clearAuthManager.redirectToClearLoginPage(window.location.hash, LoginSource.OTP_VERIFICATION_LOGIN, determineOrgId(this.loginUIManager, this.studyInfo), false, isSandbox, this.patientConsentUIManager.patientCollectedEmail, this.patientConsentUIManager.currentLanguage, undefined, this.initialScreen);
    }
}
__decorate([
    computed
], PatientLoginVerifyPhoneViewModel.prototype, "isLoggingIn", null);
__decorate([
    computed
], PatientLoginVerifyPhoneViewModel.prototype, "isClearLoginSuccess", null);
__decorate([
    observable
], PatientLoginVerifyPhoneViewModel.prototype, "hasClearError", void 0);
__decorate([
    computed
], PatientLoginVerifyPhoneViewModel.prototype, "clearLoginState", null);
